import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, API, formatGYD } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Car, Path, Money, Lightning, CheckCircle, XCircle, FileMagnifyingGlass, WhatsappLogo, Phone as PhoneIcon, MagnifyingGlass, X as ClearIcon, Play, Crown } from "@phosphor-icons/react";
import MapView from "@/components/MapView";

function KPI({ label, value, testId, accent }) {
  return (
    <div className={`border-2 border-foreground p-4 ${accent ? "bg-primary" : "bg-card"}`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground/60">{label}</div>
      <div className="font-mono text-3xl mt-1" data-testid={testId}>{value}</div>
    </div>
  );
}

function ImpersonateButton({ user, compact = false }) {
  // Admins can sign in as any non-admin user to debug their experience.
  // The backend issues a 2-hour scoped session_token and overrides the cookie.
  const disabled = !user || user.role === "admin";
  const go = async () => {
    if (disabled) return;
    try {
      await api.post(`/admin/impersonate/${user.user_id}`);
      toast.success(`Signed in as ${user.name || user.email}`);
      // Land them on the right dashboard for the impersonated role.
      const target = user.role === "driver" ? "/driver" : user.role === "rider" ? "/rider" : "/";
      window.location.href = target;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to impersonate");
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={go}
      disabled={disabled}
      data-testid={`admin-impersonate-${user.user_id}`}
      title={disabled ? "Cannot sign in as another admin" : `Sign in as ${user.name || user.email}`}
      className={`rounded-none border-2 border-foreground bg-background hover:bg-foreground hover:text-background font-mono text-[10px] uppercase tracking-wider h-7 px-2 ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      <Crown size={12} weight="fill" /> {compact ? "" : "Sign in as"}
    </Button>
  );
}


function SafetyBadge({ score, alerts = 0, tracked = 0, userId }) {
  if (score == null || tracked === 0) {
    return (
      <span
        className="font-mono text-[10px] uppercase tracking-wider text-foreground/40"
        data-testid={`admin-driver-safety-${userId}`}
        title="Not enough tracked rides yet"
      >
        —
      </span>
    );
  }
  let bg = "bg-accent text-white border-accent";
  let label = "Safe";
  if (score < 70) { bg = "bg-destructive text-destructive-foreground border-destructive"; label = "Risk"; }
  else if (score < 90) { bg = "bg-primary text-foreground border-foreground"; label = "Watch"; }
  return (
    <div className="inline-flex flex-col items-start gap-0.5" data-testid={`admin-driver-safety-${userId}`}>
      <span
        className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border-2 ${bg}`}
        title={`${alerts} speed-alert ride${alerts === 1 ? "" : "s"} of ${tracked} tracked`}
      >
        {score} · {label}
      </span>
      {alerts > 0 && (
        <span className="font-mono text-[9px] text-foreground/60">
          {alerts}/{tracked} alerts
        </span>
      )}
    </div>
  );
}

function MergeUsersTool({ users, onDone }) {
  // Admin one-shot: reconcile a duplicate account (source) onto the
  // canonical one (target). Runs a dry-run preview first so the admin
  // sees exactly how much history is about to move before committing.
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const findUser = (q) => {
    const s = q.trim().toLowerCase();
    if (!s) return null;
    return users.find(u =>
      (u.user_id || "").toLowerCase() === s ||
      (u.email || "").toLowerCase() === s
    );
  };
  const srcUser = findUser(source);
  const tgtUser = findUser(target);

  const runPreview = async () => {
    if (!srcUser || !tgtUser || srcUser.user_id === tgtUser.user_id) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/admin/users/merge?dry_run=true`,
        { source_user_id: srcUser.user_id, target_user_id: tgtUser.user_id },
      );
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const runMerge = async () => {
    if (!preview) return;
    if (!window.confirm(
      `Merge ${preview.source.email || preview.source.user_id} INTO ${preview.target.email || preview.target.user_id}?\n` +
      `The source account will be permanently deleted.`
    )) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/admin/users/merge`,
        { source_user_id: preview.source.user_id, target_user_id: preview.target.user_id },
      );
      toast.success(`Merged. ${r.data.reassigned.rides_as_driver + r.data.reassigned.rides_as_rider} rides moved.`);
      setOpen(false); setSource(""); setTarget(""); setPreview(null);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setBusy(false);
    }
  };

  const totalRefs = preview
    ? Object.values(preview.would_reassign).reduce((a, b) => a + Number(b || 0), 0)
    : 0;

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="admin-open-merge-users"
        className="rounded-none border-2 border-foreground font-mono text-[10px] uppercase tracking-wider"
      >
        Merge Duplicate Accounts
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-merge-users-dialog" className="max-w-lg rounded-none border-2 border-foreground">
          <DialogHeader>
            <DialogTitle className="font-mono uppercase tracking-widest text-sm">Merge duplicate accounts</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-foreground/70 leading-relaxed">
              Move every ride, rating, upload and community post from the
              <strong> source </strong> account onto the <strong>target</strong>,
              then delete the source. Paste a <code>user_id</code> or an email —
              the row auto-resolves from the users table above.
            </p>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider">Source (dupe — will be deleted)</Label>
              <Input
                data-testid="admin-merge-source"
                value={source}
                onChange={(e) => { setSource(e.target.value); setPreview(null); }}
                placeholder="user_xxxx or email"
                className="rounded-none border-2 border-foreground font-mono text-xs"
              />
              {source && !srcUser && <div className="text-xs text-red-500 mt-1">No match in users list</div>}
              {srcUser && (
                <div className="text-xs text-foreground/60 mt-1 font-mono" data-testid="admin-merge-source-resolved">
                  → {srcUser.name} ({srcUser.email}) · {srcUser.role || "no role"}
                </div>
              )}
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider">Target (canonical — keeps history)</Label>
              <Input
                data-testid="admin-merge-target"
                value={target}
                onChange={(e) => { setTarget(e.target.value); setPreview(null); }}
                placeholder="user_xxxx or email"
                className="rounded-none border-2 border-foreground font-mono text-xs"
              />
              {target && !tgtUser && <div className="text-xs text-red-500 mt-1">No match in users list</div>}
              {tgtUser && (
                <div className="text-xs text-foreground/60 mt-1 font-mono" data-testid="admin-merge-target-resolved">
                  → {tgtUser.name} ({tgtUser.email}) · {tgtUser.role || "no role"}
                </div>
              )}
            </div>
            {srcUser && tgtUser && srcUser.user_id === tgtUser.user_id && (
              <div className="text-xs text-red-500">Source and target must be different accounts.</div>
            )}
            {preview && (
              <div className="border-2 border-foreground p-2 space-y-1 bg-card" data-testid="admin-merge-preview">
                <div className="font-mono text-[10px] uppercase tracking-widest">Will reassign {totalRefs} references:</div>
                <div className="grid grid-cols-2 gap-x-2 text-[11px] font-mono">
                  {Object.entries(preview.would_reassign).map(([k, v]) => (
                    <div key={k} className={Number(v) ? "text-foreground" : "text-foreground/40"}>
                      {k}: {v}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={runPreview}
              disabled={busy || !srcUser || !tgtUser || srcUser?.user_id === tgtUser?.user_id}
              data-testid="admin-merge-preview-btn"
              className="rounded-none border-2 border-foreground font-mono text-[10px] uppercase tracking-wider"
            >
              {busy ? "..." : "Preview"}
            </Button>
            <Button
              onClick={runMerge}
              disabled={busy || !preview}
              data-testid="admin-merge-confirm-btn"
              className="rounded-none border-2 border-foreground bg-red-600 hover:bg-red-700 text-white font-mono text-[10px] uppercase tracking-wider"
            >
              {busy ? "..." : "Confirm merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AdminDashboard() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const validTabs = ["drivers", "users", "rides"];
  const activeTab = validTabs.includes(tab) ? tab : "drivers";
  const navActive =
    tab === "users" ? "users"
    : tab === "drivers" ? "drivers"
    : tab === "rides" ? "rides"
    : "overview";

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [rides, setRides] = useState([]);
  const [replayRide, setReplayRide] = useState(null); // {ride_id, ...full payload}
  const [replayLoading, setReplayLoading] = useState(false);

  const openReplay = async (rideId) => {
    setReplayLoading(true);
    setReplayRide({ ride_id: rideId, loading: true });
    try {
      const { data } = await api.get(`/admin/rides/${rideId}/replay`);
      setReplayRide(data);
    } catch (_e) {
      toast.error("Failed to load ride replay");
      setReplayRide(null);
    } finally {
      setReplayLoading(false);
    }
  };

  // Driver-table filters
  const [driverSearch, setDriverSearch] = useState("");
  const [driverStatusFilter, setDriverStatusFilter] = useState("all");
  const [driverOnlineFilter, setDriverOnlineFilter] = useState("all");

  const filteredDrivers = useMemo(() => {
    const q = driverSearch.trim().toLowerCase();
    return drivers.filter((d) => {
      if (driverStatusFilter !== "all" && d.status !== driverStatusFilter) return false;
      if (driverOnlineFilter === "online" && !d.online) return false;
      if (driverOnlineFilter === "offline" && d.online) return false;
      if (!q) return true;
      const hay = [
        d.name, d.email, d.phone, d.address,
        d.vehicle_make, d.vehicle_model, d.vehicle_color, d.vehicle_plate,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [drivers, driverSearch, driverStatusFilter, driverOnlineFilter]);

  const clearDriverFilters = () => {
    setDriverSearch("");
    setDriverStatusFilter("all");
    setDriverOnlineFilter("all");
  };

  const refresh = async () => {
    try {
      const [s, u, d, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/drivers"),
        api.get("/admin/rides"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setDrivers(d.data);
      setRides(r.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, []);

  const setDriverStatus = async (uid, status) => {
    try {
      await api.post(`/admin/drivers/${uid}/status`, { status });
      toast.success(`Driver ${status}`);
      refresh();
    } catch { toast.error("Failed"); }
  };

  // Inline-edit driver dialog
  const [editingDriver, setEditingDriver] = useState(null);
  const openEdit = (driver) => setEditingDriver(driver);
  const closeEdit = () => setEditingDriver(null);
  const saveEdit = async (uid, patch) => {
    try {
      await api.put(`/admin/drivers/${uid}`, patch);
      toast.success("Driver updated");
      closeEdit();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const deleteDriver = async (uid, name) => {
    try {
      const { data } = await api.delete(`/admin/drivers/${uid}`);
      const summary = Object.entries(data.deleted || {})
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`)
        .join(", ");
      toast.success(`${name || "Driver"} deleted`, {
        description: summary || "Account fully removed",
      });
      closeEdit();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar active={navActive} />
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60">Admin · Control Room</div>
        <h1 className="font-display text-4xl tracking-tighter mb-6">RideGY Overview</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KPI label="Users" value={stats?.total_users ?? "—"} testId="admin-kpi-users" />
          <KPI label="Drivers" value={stats?.drivers ?? "—"} testId="admin-kpi-drivers" />
          <KPI label="Online now" value={stats?.online_drivers ?? "—"} testId="admin-kpi-online" accent />
          <KPI label="Active rides" value={stats?.active_rides ?? "—"} testId="admin-kpi-active" />
          <KPI label="Total rides" value={stats?.total_rides ?? "—"} testId="admin-kpi-rides" />
          <KPI label="Completed" value={stats?.completed_rides ?? "—"} testId="admin-kpi-completed" />
          <KPI label="Riders" value={stats?.riders ?? "—"} testId="admin-kpi-riders" />
          <KPI label="Revenue" value={formatGYD(stats?.revenue_gyd ?? 0)} testId="admin-kpi-revenue" />
          <KPI
            label={`Speed alerts (≥${stats?.speed_alert_threshold_kmh ?? 80} km/h)`}
            value={stats?.speed_alerts ?? "—"}
            testId="admin-kpi-speed-alerts"
            accent={stats?.speed_alerts > 0}
          />
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(v) => navigate(`/admin/${v}`)}
          className="w-full"
        >
          <TabsList className="rounded-none border-2 border-foreground bg-background p-0 h-auto">
            <TabsTrigger value="drivers" data-testid="admin-tab-drivers" className="rounded-none px-4 py-2 data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Car size={16} /> Drivers
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-none px-4 py-2 data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Users size={16} /> Users
            </TabsTrigger>
            <TabsTrigger value="rides" data-testid="admin-tab-rides" className="rounded-none px-4 py-2 data-[state=active]:bg-foreground data-[state=active]:text-background">
              <Path size={16} /> Rides
            </TabsTrigger>
          </TabsList>

          <TabsContent value="drivers" className="border-2 border-foreground border-t-0">
            <DriversFilterBar
              search={driverSearch}
              onSearch={setDriverSearch}
              statusFilter={driverStatusFilter}
              onStatusFilter={setDriverStatusFilter}
              onlineFilter={driverOnlineFilter}
              onOnlineFilter={setDriverOnlineFilter}
              filteredCount={filteredDrivers.length}
              totalCount={drivers.length}
              onClear={clearDriverFilters}
            />
            <Table>
              <TableHeader>
                <TableRow className="font-mono text-[10px] uppercase tracking-wider">
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Plate</TableHead>
                  <TableHead>License</TableHead>
                  <TableHead>Contacts</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Online</TableHead>
                  <TableHead>Trips</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Safety</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDrivers.map((d) => (
                  <TableRow key={d.user_id} data-testid={`admin-driver-row-${d.user_id}`}>
                    <TableCell>
                      <button
                        type="button"
                        data-testid={`admin-driver-name-${d.user_id}`}
                        onClick={() => openEdit(d)}
                        className="text-left group"
                      >
                        <div className="font-medium flex items-center gap-1.5 group-hover:underline">
                          {d.name}
                          {d.verified && (
                            <span className="inline-flex items-center justify-center w-4 h-4 bg-accent text-white font-mono text-[9px]" title="Verified">✓</span>
                          )}
                        </div>
                        <div className="text-xs text-foreground/60">{d.email}</div>
                        {d.address && <div className="text-xs text-foreground/60 truncate max-w-[220px]">{d.address}</div>}
                      </button>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{d.phone || "—"}</TableCell>
                    <TableCell>{d.vehicle_color} {d.vehicle_make} {d.vehicle_model}</TableCell>
                    <TableCell className="font-mono">{d.vehicle_plate}</TableCell>
                    <TableCell>
                      {d.license_path ? (
                        <a
                          data-testid={`admin-license-link-${d.user_id}`}
                          href={`${API}/files/${d.license_path}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider underline"
                        >
                          <FileMagnifyingGlass size={12} /> View
                        </a>
                      ) : (
                        <span className="text-foreground/40 text-xs">none</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-accent" title="WhatsApp">
                          <WhatsappLogo size={12} weight="fill" /> {d.whatsapp_clicks || 0}
                        </span>
                        <span className="text-foreground/40">·</span>
                        <span className="inline-flex items-center gap-1" title="Call">
                          <PhoneIcon size={12} weight="fill" /> {d.call_clicks || 0}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`font-mono text-[10px] uppercase px-2 py-1 border-2 ${d.status === "approved" ? "border-accent text-accent" : d.status === "rejected" ? "border-destructive text-destructive" : "border-foreground"}`}>
                        {d.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      {d.online ? (
                        <span className="inline-flex items-center gap-1 text-accent"><Lightning size={14} weight="fill" /> Yes</span>
                      ) : <span className="text-foreground/40">No</span>}
                    </TableCell>
                    <TableCell className="font-mono">{d.total_trips}</TableCell>
                    <TableCell className="font-mono">{d.rating_avg || "—"}</TableCell>
                    <TableCell>
                      <SafetyBadge
                        score={d.safety_score}
                        alerts={d.speed_alert_rides}
                        tracked={d.tracked_rides}
                        userId={d.user_id}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end items-center">
                        <ImpersonateButton user={d} compact />
                        <Button
                          data-testid={`admin-approve-${d.user_id}`}
                          size="sm"
                          onClick={() => setDriverStatus(d.user_id, "approved")}
                          className="rounded-none h-7 bg-accent text-white hover:bg-accent/90 px-2"
                        >
                          <CheckCircle size={14} />
                        </Button>
                        <Button
                          data-testid={`admin-reject-${d.user_id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => setDriverStatus(d.user_id, "rejected")}
                          className="rounded-none h-7 border-2 border-foreground px-2"
                        >
                          <XCircle size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredDrivers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center text-foreground/60 py-8" data-testid="admin-drivers-empty">
                      {drivers.length === 0 ? "No drivers yet" : `No drivers match these filters (${drivers.length} total)`}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="users" className="border-2 border-foreground border-t-0">
            <div className="flex items-center justify-between px-3 py-2 border-b-2 border-foreground bg-card">
              <div className="font-mono text-[10px] uppercase tracking-widest text-foreground/60">
                {users.length} user{users.length === 1 ? "" : "s"}
              </div>
              <MergeUsersTool users={users} onDone={refresh} />
            </div>
            <Table>
              <TableHeader>
                <TableRow className="font-mono text-[10px] uppercase tracking-wider">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Verified</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                    <TableCell className="flex items-center gap-1.5">
                      {u.name}
                      {u.verified && (
                        <span className="inline-flex items-center justify-center w-4 h-4 bg-accent text-white font-mono text-[9px]" title="Verified">✓</span>
                      )}
                    </TableCell>
                    <TableCell className="text-foreground/70">{u.email}</TableCell>
                    <TableCell className="font-mono text-xs">{u.phone || "—"}</TableCell>
                    <TableCell>
                      <span className="font-mono text-[10px] uppercase px-2 py-1 border-2 border-foreground">{u.role || "—"}</span>
                    </TableCell>
                    <TableCell>
                      {u.verified ? <span className="text-accent font-mono text-xs">YES</span> : <span className="text-foreground/40 text-xs">no</span>}
                    </TableCell>
                    <TableCell>
                      {u.id_path ? (
                        <a
                          data-testid={`admin-id-link-${u.user_id}`}
                          href={`${API}/files/${u.id_path}`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-[10px] uppercase tracking-wider underline"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-foreground/40 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{(u.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-right">
                      <ImpersonateButton user={u} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="rides" className="border-2 border-foreground border-t-0">
            <Table>
              <TableHeader>
                <TableRow className="font-mono text-[10px] uppercase tracking-wider">
                  <TableHead>Rider</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Distance</TableHead>
                  <TableHead>Fare</TableHead>
                  <TableHead>Top speed</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Replay</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rides.map((r) => (
                  <TableRow key={r.ride_id} data-testid={`admin-ride-row-${r.ride_id}`}>
                    <TableCell>{r.rider_name}</TableCell>
                    <TableCell>{r.driver_name || <span className="text-foreground/40">—</span>}</TableCell>
                    <TableCell className="max-w-[280px] truncate">{r.pickup.address} → {r.dropoff.address}</TableCell>
                    <TableCell className="font-mono">{r.distance_km} km</TableCell>
                    <TableCell className="font-mono">{formatGYD(r.fare_gyd)}</TableCell>
                    <TableCell className="font-mono">
                      {r.tracked_points > 0 ? (
                        r.speed_alert ? (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 border-2 border-destructive bg-destructive text-destructive-foreground text-[10px] uppercase tracking-wider"
                            data-testid={`admin-ride-speed-alert-${r.ride_id}`}
                            title={`Driver exceeded ${r.speed_alert_threshold_kmh} km/h on this ride`}
                          >
                            <Lightning size={10} weight="fill" />
                            {Math.round(r.max_speed_kmh)} km/h
                          </span>
                        ) : (
                          <span data-testid={`admin-ride-top-speed-${r.ride_id}`}>
                            {Math.round(r.max_speed_kmh)} km/h
                          </span>
                        )
                      ) : (
                        <span className="text-foreground/40">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-[10px] uppercase px-2 py-1 border-2 border-foreground">{r.status}</span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openReplay(r.ride_id)}
                        data-testid={`admin-ride-replay-${r.ride_id}`}
                        className="border-2 border-foreground bg-background hover:bg-foreground hover:text-background font-mono text-[10px] uppercase tracking-wider h-7 px-2"
                      >
                        <Play size={12} weight="fill" /> Replay
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {rides.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-foreground/60 py-8">No rides yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </div>
      <EditDriverDialog driver={editingDriver} onClose={closeEdit} onSave={saveEdit} onDelete={deleteDriver} />
      <RideReplayDialog ride={replayRide} loading={replayLoading} onClose={() => setReplayRide(null)} />
    </div>
  );
}


function RideReplayDialog({ ride, loading, onClose }) {
  const open = !!ride;
  const positions = useMemo(() => {
    if (!ride?.history) return [];
    return ride.history
      .filter((p) => p && p.lat != null && p.lng != null)
      .map((p) => [p.lat, p.lng]);
  }, [ride]);

  const center = useMemo(() => {
    if (positions.length > 0) return { lat: positions[0][0], lng: positions[0][1] };
    if (ride?.pickup) return { lat: ride.pickup.lat, lng: ride.pickup.lng };
    return { lat: 6.8013, lng: -58.1551 };
  }, [positions, ride]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[95vw] border-2 border-foreground" data-testid="admin-ride-replay-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tighter">Ride replay</DialogTitle>
        </DialogHeader>
        {loading || ride?.loading ? (
          <div className="py-12 text-center font-mono text-sm text-foreground/60">Loading…</div>
        ) : ride ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div className="border-2 border-foreground p-2">
                <div className="font-mono text-[9px] uppercase tracking-wider text-foreground/60">Rider</div>
                <div className="truncate">{ride.rider_name || "—"}</div>
              </div>
              <div className="border-2 border-foreground p-2">
                <div className="font-mono text-[9px] uppercase tracking-wider text-foreground/60">Driver</div>
                <div className="truncate">{ride.driver_name || "—"}</div>
              </div>
              <div className="border-2 border-foreground p-2">
                <div className="font-mono text-[9px] uppercase tracking-wider text-foreground/60">Distance</div>
                <div className="font-mono">{ride.distance_km ?? "—"} km</div>
              </div>
              <div className="border-2 border-foreground p-2">
                <div className="font-mono text-[9px] uppercase tracking-wider text-foreground/60">Fare</div>
                <div className="font-mono">{formatGYD(ride.fare_gyd || 0)}</div>
              </div>
            </div>
            <div className="text-xs font-mono text-foreground/70" data-testid="admin-ride-replay-point-count">
              {ride.point_count || 0} GPS point{(ride.point_count || 0) === 1 ? "" : "s"} recorded
              {ride.status ? ` · ${ride.status}` : ""}
            </div>
            <div className="border-2 border-foreground h-[55vh]" data-testid="admin-ride-replay-map">
              <MapView
                center={center}
                zoom={14}
                pickup={ride.pickup}
                dropoff={ride.dropoff}
                showRoute
                routeCoords={positions.length >= 2 ? positions : null}
                dataTestId="admin-replay-map-inner"
              />
            </div>
            {positions.length === 0 && (
              <div className="font-mono text-xs text-foreground/60 border-2 border-dashed border-foreground/40 p-3">
                No GPS history was recorded for this ride. Trail recording was enabled on 2026-06-20 — older rides won&apos;t have data.
              </div>
            )}
          </div>
        ) : null}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-2 border-foreground"
            data-testid="admin-ride-replay-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function DriversFilterBar({
  search,
  onSearch,
  statusFilter,
  onStatusFilter,
  onlineFilter,
  onOnlineFilter,
  filteredCount,
  totalCount,
  onClear,
}) {
  const STATUSES = ["all", "pending", "approved", "rejected"];
  const ONLINE = ["all", "online", "offline"];
  const hasFilters = search || statusFilter !== "all" || onlineFilter !== "all";

  return (
    <div className="border-b-2 border-foreground bg-secondary/40 p-3 flex flex-wrap items-center gap-2" data-testid="admin-drivers-filter-bar">
      <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-md border-2 border-foreground bg-background h-9 px-3">
        <MagnifyingGlass size={14} weight="bold" className="text-foreground/60 shrink-0" />
        <input
          data-testid="admin-drivers-search-input"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search name, email, phone, plate, vehicle…"
          className="flex-1 bg-transparent outline-none text-sm font-medium placeholder:text-foreground/40 min-w-0"
        />
        {search && (
          <button
            type="button"
            data-testid="admin-drivers-search-clear"
            onClick={() => onSearch("")}
            className="text-foreground/50 hover:text-foreground"
            aria-label="Clear search"
          >
            <ClearIcon size={12} weight="bold" />
          </button>
        )}
      </div>

      <FilterChipGroup
        testIdPrefix="admin-drivers-filter-status"
        label="Status"
        options={STATUSES}
        value={statusFilter}
        onChange={onStatusFilter}
      />

      <FilterChipGroup
        testIdPrefix="admin-drivers-filter-online"
        label="Online"
        options={ONLINE}
        value={onlineFilter}
        onChange={onOnlineFilter}
      />

      <div className="ml-auto flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-foreground/70">
        <span data-testid="admin-drivers-count">
          {filteredCount === totalCount ? `${totalCount} total` : `${filteredCount} of ${totalCount}`}
        </span>
        {hasFilters && (
          <Button
            data-testid="admin-drivers-clear-filters"
            size="sm"
            variant="outline"
            onClick={onClear}
            className="rounded-none h-7 border-2 border-foreground text-[10px] uppercase tracking-wider px-2"
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}

function FilterChipGroup({ testIdPrefix, label, options, value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-foreground/60 mr-1">{label}:</span>
      {options.map((opt) => (
        <button
          key={opt}
          data-testid={`${testIdPrefix}-${opt}`}
          onClick={() => onChange(opt)}
          className={`font-mono text-[10px] uppercase tracking-wider border-2 border-foreground h-7 px-2 transition-colors ${
            value === opt ? "bg-foreground text-background" : "bg-background hover:bg-secondary"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}



function EditDriverDialog({ driver, onClose, onSave, onDelete }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (driver) {
      setForm({
        name: driver.name || "",
        phone: driver.phone || "",
        address: driver.address || "",
        vehicle_make: driver.vehicle_make || "",
        vehicle_model: driver.vehicle_model || "",
        vehicle_color: driver.vehicle_color || "",
        vehicle_plate: driver.vehicle_plate || "",
        bio: driver.bio || "",
        status: driver.status || "pending",
      });
      setConfirmingDelete(false);
    }
  }, [driver]);

  if (!driver) return null;
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    await onSave(driver.user_id, form);
    setSaving(false);
  };

  return (
    <Dialog open={!!driver} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="admin-edit-driver-dialog"
        className="max-w-2xl rounded-none border-2 border-foreground p-0 gap-0 shadow-[6px_6px_0_0_#FFB800]"
      >
        <DialogHeader className="p-5 border-b-2 border-foreground">
          <DialogTitle className="font-display text-2xl tracking-tighter">
            Edit driver · <span className="font-mono text-base">{driver.user_id}</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name">
              <Input data-testid="edit-driver-name" value={form.name} onChange={change("name")} className="rounded-none border-2 border-foreground" />
            </Field>
            <Field label="Phone">
              <Input data-testid="edit-driver-phone" value={form.phone} onChange={change("phone")} className="rounded-none border-2 border-foreground" />
            </Field>
          </div>
          <Field label="Address">
            <Input data-testid="edit-driver-address" value={form.address} onChange={change("address")} className="rounded-none border-2 border-foreground" />
          </Field>

          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 pt-2">Vehicle</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Make">
              <Input data-testid="edit-driver-vmake" value={form.vehicle_make} onChange={change("vehicle_make")} className="rounded-none border-2 border-foreground" />
            </Field>
            <Field label="Model">
              <Input data-testid="edit-driver-vmodel" value={form.vehicle_model} onChange={change("vehicle_model")} className="rounded-none border-2 border-foreground" />
            </Field>
            <Field label="Color">
              <Input data-testid="edit-driver-vcolor" value={form.vehicle_color} onChange={change("vehicle_color")} className="rounded-none border-2 border-foreground" />
            </Field>
            <Field label="Plate">
              <Input data-testid="edit-driver-vplate" value={form.vehicle_plate} onChange={change("vehicle_plate")} className="rounded-none border-2 border-foreground font-mono uppercase" />
            </Field>
          </div>

          <Field label="Bio">
            <Textarea
              data-testid="edit-driver-bio"
              value={form.bio}
              onChange={change("bio")}
              rows={3}
              maxLength={400}
              className="rounded-none border-2 border-foreground"
            />
          </Field>

          <Field label="Status">
            <div className="flex gap-2">
              {["pending", "approved", "rejected"].map((s) => (
                <button
                  type="button"
                  key={s}
                  data-testid={`edit-driver-status-${s}`}
                  onClick={() => setForm({ ...form, status: s })}
                  className={`font-mono text-[10px] uppercase tracking-wider border-2 border-foreground h-8 px-3 ${
                    form.status === s ? "bg-foreground text-background" : "bg-background hover:bg-secondary"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </Field>

          {/* Danger zone — fully delete the driver and every trace of them */}
          <div className="border-2 border-destructive bg-destructive/5 p-3 mt-4" data-testid="edit-driver-danger-zone">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-destructive font-bold">Danger zone</div>
            <p className="text-xs text-foreground/80 mt-1 mb-3">
              Permanently delete this driver and every trace of them: profile, rides, reviews, contact-click logs, license / ID / gallery files, and any open sessions. This cannot be undone.
            </p>
            {!confirmingDelete ? (
              <button
                type="button"
                data-testid="edit-driver-delete-btn"
                onClick={() => setConfirmingDelete(true)}
                className="font-mono text-[10px] uppercase tracking-wider border-2 border-destructive text-destructive bg-background hover:bg-destructive hover:text-destructive-foreground h-9 px-3"
              >
                Delete account
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-destructive">Sure? This is permanent.</span>
                <button
                  type="button"
                  data-testid="edit-driver-delete-confirm"
                  disabled={deleting}
                  onClick={async () => {
                    setDeleting(true);
                    await onDelete(driver.user_id, driver.name);
                    setDeleting(false);
                  }}
                  className="font-mono text-[10px] uppercase tracking-wider border-2 border-destructive bg-destructive text-destructive-foreground h-9 px-3 disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Yes, delete forever"}
                </button>
                <button
                  type="button"
                  data-testid="edit-driver-delete-cancel"
                  onClick={() => setConfirmingDelete(false)}
                  className="font-mono text-[10px] uppercase tracking-wider border-2 border-foreground h-9 px-3"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </form>

        <DialogFooter className="p-5 border-t-2 border-foreground gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="edit-driver-cancel"
            onClick={onClose}
            className="rounded-none border-2 border-foreground"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="edit-driver-save"
            onClick={submit}
            disabled={saving}
            className="rounded-none bg-foreground text-background hover:bg-foreground/90"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="font-mono text-[10px] uppercase tracking-[0.15em] text-foreground/60">{label}</Label>
      {children}
    </div>
  );
}
